// Generic modal scaffold shared by ConfirmModal (D-086), BlockPickerModal
// (D-093), NewEmailModal (P-032), and LinkInsertionModal (D-097).
// Per OC §3.3 + D-093 forward note — extracted at the 3rd consumer-trigger.
//
// **Responsibilities pinned to the shell.**
// - Conditional render (`if (!open) return null` short-circuit).
// - Overlay + panel chrome with `role="dialog"`, `aria-modal="true"`,
//   `aria-labelledby={labelledBy}`.
// - Click-outside-to-cancel: overlay click fires `onCancel`; panel
//   click `stopPropagation` keeps interior interactions from bubbling.
// - ESC-key cancel: `keydown` listener on `window` while open.
// - **Focus trap (P-036).** On open, capture the previously-focused
//   element; cycle Tab / Shift+Tab within the panel's focusables; on
//   close, restore focus to the previously-focused element.
// - **Capture-via-`useLayoutEffect`** so the snapshot fires after DOM
//   commit but before child `useEffect` autofocus runs (parent layout
//   effect → browser paint → child effect order). Constraint: consumer
//   autofocus must use `useEffect`, not `useLayoutEffect` (no consumer
//   does today; documented here so future contributors don't regress).
//
// **Responsibilities the consumer keeps.**
// - Auto-focus a specific element on open (each modal focuses a
//   different button — Confirm-Cancel-Picker — so the focus target is
//   the consumer's call). Consumers attach their own `useEffect` +
//   `ref.current?.focus()`.
// - Header / footer / body content. `<Modal>` is a transparent shell;
//   consumers compose their own panel contents via `children`.
//
// **`size: 'sm' | 'lg'` preset.** Two empirically-distinguishable
// sizes across the V1 modal set:
// - `'sm'` (default) = `max-w-md` (448px) panel, no internal scroll —
//   used by short-message ConfirmModal.
// - `'lg'` = `max-w-2xl` (672px) panel + `max-h-full overflow-y-auto` +
//   outer `py-6` extra vertical breathing room — used by the picker
//   modals (Block + new-email) which list arbitrary-length content.
// More sizes can be added if a 3rd shape arrives at P-033 / P-035; keep
// the contract narrow until then.

import type { ComponentChildren } from 'preact'
import { useEffect, useLayoutEffect, useRef } from 'preact/hooks'

const OVERLAY_BASE = 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'
const OVERLAY_SM = OVERLAY_BASE
const OVERLAY_LG = `${OVERLAY_BASE} py-6`

const PANEL_BASE = 'rounded-md bg-white p-6 shadow-lg'
const PANEL_SM = `w-full max-w-md ${PANEL_BASE}`
const PANEL_LG = `max-h-full w-full max-w-2xl overflow-y-auto ${PANEL_BASE}`

// WAI-ARIA Authoring Practices focusable selector. `[tabindex]:not([tabindex="-1"])`
// catches programmatic-focusable elements (e.g., `<main tabIndex={-1}>` is excluded).
// Disabled-element + visibility filtering is applied in the keydown handler.
const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

export interface ModalProps {
  readonly open: boolean
  readonly onCancel: () => void
  readonly labelledBy: string
  readonly size?: 'sm' | 'lg'
  readonly children: ComponentChildren
}

export function Modal({ open, onCancel, labelledBy, size = 'sm', children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const prevFocusRef = useRef<HTMLElement | null>(null)

  // Focus capture/restore — runs synchronously after DOM commit so the
  // snapshot beats any child `useEffect` autofocus (consumers focus a
  // specific button on open).
  useLayoutEffect(() => {
    if (!open) return
    prevFocusRef.current = document.activeElement as HTMLElement | null
    return () => {
      const prev = prevFocusRef.current
      prevFocusRef.current = null
      if (prev && document.contains(prev)) {
        prev.focus()
      }
    }
  }, [open])

  // Keyboard handler — Escape cancels, Tab/Shift+Tab cycles within the
  // panel's focusables.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
        return
      }
      if (e.key !== 'Tab') return
      const panel = panelRef.current
      /* v8 ignore next -- panelRef is set by the time the keydown listener fires; defensive null check */
      if (!panel) return
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled'),
      )
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onCancel])

  if (!open) return null

  const overlayClass = size === 'lg' ? OVERLAY_LG : OVERLAY_SM
  const panelClass = size === 'lg' ? PANEL_LG : PANEL_SM

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      class={overlayClass}
      onClick={onCancel}
    >
      <div ref={panelRef} class={panelClass} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}
